import { useState } from "react";
import { toast } from "sonner";
import { MailCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { ResendCode } from "@/components/ResendCode";

export default function VerifyEmailStep({ email, onVerified, sandbox = false }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const verify = async (e) => {
    e?.preventDefault();
    if (code.trim().length !== 6) { toast.error("Enter the 6-digit code from your email."); return; }
    if (sandbox) { toast.success("Email confirmed! (sandbox)"); onVerified({}); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/winners/verify", { email, code: code.trim() });
      toast.success("Email confirmed!");
      onVerified(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "That code didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (sandbox) { toast.info("Sandbox: no code is sent. Enter any 6 digits."); return; }
    try {
      const { data } = await api.post("/winners/resend-verification", { email });
      toast.success("New code sent. Check your inbox.");
      if (data.debug_code) toast.info(`Dev code: ${data.debug_code}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend the code.");
    }
  };

  return (
    <div className="animate-pop-in mx-auto max-w-xl text-center" data-testid="verify-email-step">
      <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-sbg-turquoise text-sbg-burgundy">
        <MailCheck className="h-8 w-8" />
      </span>
      <h2 className="mt-5 font-display text-3xl font-black text-sbg-burgundy">Confirm your email</h2>
      <p className="mt-3 text-sbg-burgundy/70">
        We sent a 6-digit code to <span className="font-bold text-sbg-burgundy">{email}</span>. Enter it below to publish your page.
        This keeps your page safe so only you can create or change it.
      </p>
      <form onSubmit={verify} className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <input
          data-testid="verify-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          placeholder="123456"
          className="w-48 rounded-xl border border-sbg-raspberry/20 bg-white px-4 py-3 text-center font-display text-2xl font-black tracking-[0.4em] text-sbg-burgundy outline-none focus:border-sbg-fuchsia"
        />
        <button type="submit" data-testid="verify-code-btn" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-6 py-3 font-bold text-white disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm &amp; publish
        </button>
      </form>
      <div className="mt-4"><ResendCode onResend={resend} testId="verify-resend-btn" /></div>
    </div>
  );
}
